<template>
  <div class="api-search-page">
    <div class="api-search-card">
      <div class="api-search-head">
        <h2 class="api-search-title">检索 API</h2>
        <div class="api-search-toolbar">
          <span class="api-search-label">数据集</span>
          <a-select v-model:value="selectedLabelGroup" style="width:180px" @change="onDatasetChange">
            <a-select-option v-for="m in datasetOptions" :key="m.value" :value="m.value">{{ m.label }}</a-select-option>
          </a-select>
          <span class="api-search-endpoint">{{ apiEndpoint }}</span>
          <a-space style="margin-left:auto">
            <a-button @click="resetFilterForm">重置</a-button>
            <a-button type="primary" :loading="queryLoading" @click="submitQuery">执行查询</a-button>
          </a-space>
        </div>
      </div>

      <a-spin :spinning="configLoading">
        <div class="adm-api-section">
          <h3 class="adm-api-section-title">筛选条件</h3>
          <a-table class="adm-table" :columns="cols" :data-source="filterRows" :pagination="false" row-key="rowKey" size="small">
            <template #bodyCell="{ column, record, index }">
              <template v-if="column.key === 'field'">
                <a-select
                  v-model:value="record.field"
                  size="small"
                  style="width:100%"
                  :dropdown-match-select-width="false"
                  popup-class-name="advanced-search-select-dropdown"
                  @change="onRowFieldChange(record)"
                >
                  <a-select-option v-for="f in availableFields(record)" :key="f.field" :value="f.field">{{ f.label }}</a-select-option>
                </a-select>
              </template>
              <template v-else-if="column.key === 'clause'">
                <a-select
                  v-model:value="record.clause"
                  size="small"
                  style="width:100%"
                  :dropdown-match-select-width="false"
                  popup-class-name="advanced-search-select-dropdown"
                  @change="syncBody"
                >
                  <a-select-option v-for="c in clauseOptions(record.field)" :key="c.value" :value="c.value">{{ c.label }}</a-select-option>
                </a-select>
              </template>
              <template v-else-if="column.key === 'match_mode'">
                <a-select
                  v-model:value="record.match_mode"
                  size="small"
                  style="width:100%"
                  :dropdown-match-select-width="false"
                  popup-class-name="advanced-search-select-dropdown"
                  @change="onRowMatchModeChange(record)"
                >
                  <a-select-option v-for="m in matchModeOptions(record.field)" :key="m.value" :value="m.value">{{ m.label }}</a-select-option>
                </a-select>
              </template>
              <template v-else-if="column.key === 'value'">
                <div v-if="isDateRangeRow(record)" style="display:flex;gap:4px;align-items:center">
                  <a-date-picker
                    size="small"
                    style="flex:1;min-width:0"
                    :value="record.value || null"
                    value-format="YYYY-MM-DD"
                    placeholder="开始"
                    @change="(_, dateString) => { record.value = dateString || ''; syncBody() }"
                  />
                  <a-date-picker
                    size="small"
                    style="flex:1;min-width:0"
                    :value="record.valueEnd || null"
                    value-format="YYYY-MM-DD"
                    placeholder="结束"
                    @change="(_, dateString) => { record.valueEnd = dateString || ''; syncBody() }"
                  />
                </div>
                <a-date-picker
                  v-else-if="isDateFieldRow(record)"
                  size="small"
                  style="width:100%"
                  :value="record.value || null"
                  value-format="YYYY-MM-DD"
                  placeholder="选择日期"
                  :disabled="record.match_mode === 'exists'"
                  @change="(_, dateString) => { record.value = dateString || ''; syncBody() }"
                />
                <div v-else-if="isTreeFieldRow(record)" class="advanced-search-tree">
                  <TreeFilter
                    :options="treeOptions(record.field)"
                    :model-value="Array.isArray(record.value) ? record.value : []"
                    @update:model-value="(v) => { record.value = Array.isArray(v) ? v : []; syncBody() }"
                  />
                </div>
                <a-select
                  v-else-if="isSelectFieldRow(record)"
                  v-model:value="record.value"
                  size="small"
                  style="width:100%"
                  allow-clear
                  show-search
                  option-filter-prop="label"
                  :mode="isMultiSelectRow(record) ? 'multiple' : undefined"
                  :max-tag-count="isMultiSelectRow(record) ? 2 : undefined"
                  :placeholder="getValuePlaceholder(record, searchConfig)"
                  :disabled="record.match_mode === 'exists'"
                  :dropdown-match-select-width="false"
                  popup-class-name="advanced-search-select-dropdown"
                  :options="selectOptions(record.field)"
                  @change="syncBody"
                />
                <a-input
                  v-else
                  v-model:value="record.value"
                  size="small"
                  :placeholder="getValuePlaceholder(record, searchConfig)"
                  @input="syncBody"
                />
              </template>
              <template v-else-if="column.key === 'actions'">
                <a-button type="link" size="small" danger @click="removeFilterRow(index)">删除</a-button>
              </template>
            </template>
          </a-table>
          <a-button type="dashed" block style="margin-top:8px" :disabled="!searchConfig || !canAddRow" @click="addFilterRow">+ 添加条件</a-button>
        </div>

        <div class="adm-api-section">
          <h3 class="adm-api-section-title">请求体 JSON（可手工修改）</h3>
          <a-textarea v-model:value="requestBodyText" :rows="8" class="adm-mono" spellcheck="false" />
        </div>

        <div v-if="apiResult" class="adm-api-section">
          <h3 class="adm-api-section-title">响应结果</h3>
          <pre class="adm-code-block">{{ apiResult.text }}</pre>
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue'
import { getAdvancedSearchConfig, getFilterOptions, postAdvancedSearch, getAdvancedSearchPath } from '@/api/filterApi'
import TreeFilter from '@/components/filters/TreeFilter.vue'
import {
  createDefaultRows,
  createEmptyRow,
  buildAdvancedSearchBody,
  getValuePlaceholder,
  getClauseOptions,
  getMatchModeOptions,
  onFieldChange,
  onMatchModeChange,
  isDateField,
  isDateRangeField,
  isSelectField,
  isTreeField,
  isMultiValueSelect,
  getFieldSelectOptions,
  getFieldTreeOptions,
  enrichAdvancedSearchConfigWithFilters,
  normalizeSearchEndpoint,
} from '@/utils/advancedSearch'

const DATASET_OPTIONS = [
  { value: 1, label: '市场端' },
  { value: 2, label: '国家政策' },
  { value: 3, label: '招投标信息' },
  { value: 4, label: '前沿技术' },
  { value: 5, label: '器械注册查询' },
  { value: 6, label: '临床技术' },
]

export default {
  name: 'ApiSearchView',
  components: {
    TreeFilter,
  },
  data() {
    return {
      selectedLabelGroup: 5,
      searchConfig: null,
      configLoading: false,
      queryLoading: false,
      filterRows: [],
      requestBodyText: '{}',
      apiResult: null,
      datasetOptions: DATASET_OPTIONS,
      cols: [
        { title: '检索项', key: 'field' },
        { title: '逻辑', key: 'clause', width: 120 },
        { title: '匹配', key: 'match_mode', width: 140 },
        { title: '检索词', key: 'value' },
        { title: '', key: 'actions', width: 56 },
      ],
    }
  },
  computed: {
    indexFields() {
      return this.searchConfig?.fields || []
    },
    canAddRow() {
      const used = new Set(this.filterRows.map((r) => r.field).filter(Boolean))
      return this.indexFields.some((f) => !used.has(f.field))
    },
    apiEndpoint() {
      const ep = this.searchConfig?.dataset?.search_endpoint
      if (ep) return normalizeSearchEndpoint(ep).replace(/^\/v1/, '/api/v1')
      return `/api${getAdvancedSearchPath(this.selectedLabelGroup)}`
    },
  },
  mounted() {
    document.title = 'PharmAID · 检索 API'
    this.loadConfig()
  },
  beforeUnmount() {
    document.title = 'PharmAID'
  },
  methods: {
    getValuePlaceholder,
    availableFields(row) {
      const usedByOthers = new Set(
        this.filterRows
          .filter((r) => r !== row && r.field)
          .map((r) => r.field),
      )
      return this.indexFields.filter((f) => f.field === row.field || !usedByOthers.has(f.field))
    },
    clauseOptions(field) {
      return getClauseOptions(this.searchConfig, field)
    },
    matchModeOptions(field) {
      return getMatchModeOptions(this.searchConfig, field)
    },
    isDateRangeRow(row) {
      return row.match_mode === 'range' && isDateRangeField(this.searchConfig, row.field)
    },
    isDateFieldRow(row) {
      return row.match_mode !== 'exists' && isDateField(this.searchConfig, row.field)
    },
    isTreeFieldRow(row) {
      return row.match_mode !== 'exists' && isTreeField(this.searchConfig, row.field)
    },
    isSelectFieldRow(row) {
      return row.match_mode !== 'exists' && isSelectField(this.searchConfig, row.field)
    },
    isMultiSelectRow(row) {
      return isMultiValueSelect(this.searchConfig, row.field, row.match_mode)
    },
    selectOptions(field) {
      return getFieldSelectOptions(this.searchConfig, field)
    },
    treeOptions(field) {
      return getFieldTreeOptions(this.searchConfig, field)
    },
    async loadConfig() {
      this.configLoading = true
      this.apiResult = null
      try {
        const [configRes, filterRes] = await Promise.all([
          getAdvancedSearchConfig(this.selectedLabelGroup),
          getFilterOptions(this.selectedLabelGroup).catch(() => null),
        ])
        const filters = filterRes?.errno === 0 ? (filterRes.data || []) : []
        this.searchConfig = enrichAdvancedSearchConfigWithFilters(configRes.data, filters)
        this.filterRows = createDefaultRows(this.searchConfig, 3).map((r, i) => ({ ...r, rowKey: `r-${i}` }))
        if (!this.filterRows.length) {
          this.filterRows = [{ ...createEmptyRow(this.searchConfig), rowKey: 'r-0' }]
        }
        this.syncBody()
      } catch (e) {
        this.searchConfig = null
        this.filterRows = []
        this.requestBodyText = '{}'
      } finally {
        this.configLoading = false
      }
    },
    onDatasetChange() {
      this.loadConfig()
    },
    onRowFieldChange(row) {
      onFieldChange(row, this.searchConfig)
      this.syncBody()
    },
    onRowMatchModeChange(row) {
      onMatchModeChange(row, this.searchConfig)
      this.syncBody()
    },
    syncBody() {
      this.requestBodyText = JSON.stringify(
        buildAdvancedSearchBody(this.filterRows, {
          page: 1,
          limit: 20,
          config: this.searchConfig,
        }),
        null,
        2,
      )
    },
    addFilterRow() {
      if (!this.searchConfig || !this.canAddRow) return
      const used = new Set(this.filterRows.map((r) => r.field).filter(Boolean))
      const nextField = this.indexFields.find((f) => !used.has(f.field))
      if (!nextField) return
      const row = { ...createEmptyRow(this.searchConfig), rowKey: `r-${Date.now()}` }
      row.field = nextField.field
      onFieldChange(row, this.searchConfig)
      this.filterRows.push(row)
      this.syncBody()
    },
    removeFilterRow(i) {
      if (this.filterRows.length <= 1) return
      this.filterRows.splice(i, 1)
      this.syncBody()
    },
    resetFilterForm() {
      this.loadConfig()
      message.success('已重置')
    },
    async submitQuery() {
      let body
      try {
        body = JSON.parse(this.requestBodyText)
      } catch {
        message.error('JSON 格式错误')
        return
      }
      if (body.advance_query !== 1) {
        body.advance_query = 1
      }
      this.queryLoading = true
      try {
        const res = await postAdvancedSearch(this.selectedLabelGroup, body)
        this.apiResult = {
          text: JSON.stringify({
            data: res.data,
            errno: res.errno,
            errmsg: res.errmsg,
            status: res.status,
          }, null, 2),
        }
        message.success('查询成功')
      } catch (e) {
        this.apiResult = { text: String(e.message || e) }
      } finally {
        this.queryLoading = false
      }
    },
  },
}
</script>

<style scoped>
.api-search-page {
  min-height: 100%;
}
.api-search-card {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}
.api-search-head {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.api-search-title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.api-search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.api-search-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}
.api-search-endpoint {
  font-size: 12px;
  font-family: ui-monospace, monospace;
  color: rgba(0, 0, 0, 0.45);
}
</style>

<style>
@import '@/styles/admin-shell.css';
</style>
